/** Typed configuration forms for each node type. */
import { Fragment, useState, type ReactNode } from 'react';
import { CalendarClock, Loader2, Plus, Trash2 } from 'lucide-react';
import {
  DEFAULT_SCHEDULE_TIMEZONE, FORMAT_CLASSES, NODE_TYPES, TRIGGER_LABELS, describeDateRule, formatInZone,
  type AutomationGraph, type AutomationNode, type Condition, type DateFiringPreviewDTO, type ExportDestinationDTO, type FieldDefinition, type FormatClass, type LiveSourceDTO, type Mapping,
  type MasterChoiceOrAll, type MasterVariantDTO, type MetadataDateTrigger, type QcProfileDTO, type QcTaskAssignee, type RoleDTO, type SaveAs, type StorageLocationSummary, type Trigger,
  type WebhookEndpointDTO,
} from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { PresetSelect } from '../../components/PresetSelect';
import { DestinationSelect } from '../../components/DestinationSelect';
import { SidecarFormats } from '../../subtitles/SidecarFormats';
import { DEFAULT_SIDECAR_FORMATS } from '@prism/contracts';
import { useLiveSources } from '../admin/LiveSourcesAdmin';
import { Link } from 'react-router-dom';
import { AssignForDeliveryEditor, DeliveryCheckFields } from './portal-steps';
import { DetectShotsEditor } from './detect-shots-step';
// 1.6.0 (docs/dev/mcp-1.6.0-build.md): site services (W) and AI description (A).
import { SiteServiceCheckFields, SiteServiceEditor, siteServiceStepDefaults } from './site-service-step';
import { CallWebhookJsonOptions, ForEachEditor, UpdateMetadataSuggestionOption, UpdateSegmentEditor, forEachItemPaths } from './ai-steps';
import {
  useAssignees, useExportDestinations, useMasterVariants, useMe, useQcProfiles, useRoles, useSchema, useSequences, useStorage, useTranscodeProfiles, useTranscoders, useWatchFolders, useWebhookEndpoints,
  type SequenceSummary, type TranscodeProfilesDTO, type TranscoderStatus,
} from '../../api/hooks';

type Patch = (n: AutomationNode) => void;

/** What a Date in metadata trigger's conditions can test at firing time (1.0.0-rc.10): the asset, and the date that came due. */
function assetPaths(fields: Array<{ id: string; label: string }>): Array<{ path: string; label: string }> {
  return [
    ...fields.map((f) => ({ path: `asset.metadata.${f.id}`, label: `Asset · ${f.label}` })),
    { path: 'asset.title', label: 'Asset · title' },
    { path: 'asset.status', label: 'Asset · status' },
    { path: 'asset.className', label: 'Asset · class' },
    { path: 'asset.qcStatus', label: 'Asset · QC result (passed, warnings, failed, error)' },
    { path: 'asset.mediaKind', label: 'Asset · media kind' },
    { path: 'asset.durationSeconds', label: 'Asset · duration (s)' },
    { path: 'asset.height', label: 'Asset · height (px)' },
  ];
}

/** Whether the workflow starts when a master is added (1.2.0). */
const masterAdded = (graph: AutomationGraph) => graph.nodes.some((n) => n.type === 'trigger' && n.trigger.type === 'master_added');

function contextPaths(graph: AutomationGraph, fields: Array<{ id: string; label: string }>): Array<{ path: string; label: string }> {
  const dated = graph.nodes.some((n) => n.type === 'trigger' && n.trigger.type === 'metadata_date');
  return [
    ...fields.map((f) => ({ path: `asset.metadata.${f.id}`, label: `Asset · ${f.label}` })),
    { path: 'asset.title', label: 'Asset · title' },
    { path: 'asset.mediaKind', label: 'Asset · media kind' },
    { path: 'asset.durationSeconds', label: 'Asset · duration (s)' },
    { path: 'asset.height', label: 'Asset · height (px)' },
    { path: 'asset.className', label: 'Asset · class' },
    { path: 'asset.qcStatus', label: 'Asset · QC result (passed, warnings, failed, error)' },
    // 1.2.0: the current version's masters (has master / no master).
    { path: 'asset.masters', label: 'Asset · masters' },
    { path: 'trigger.type', label: 'Trigger · type' },
    { path: 'trigger.payload', label: 'Trigger · webhook payload (add .field)' },
    ...(dated ? [{ path: 'trigger.date', label: 'Trigger · the date that came due' }, { path: 'trigger.firingAt', label: 'Trigger · when it fired (UTC)' }] : []),
    ...(masterAdded(graph) ? [
      { path: 'trigger.payload.variantId', label: 'Trigger · the new master’s variant' },
      { path: 'trigger.payload.formatClass', label: 'Trigger · the new master’s format (UHD, HD…)' },
      { path: 'master.label', label: 'New master · label' },
    ] : []),
    { path: 'asset.status', label: 'Asset · status' },
    { path: 'sequence.name', label: 'Sequence · name' },
    { path: 'sequence.durationSeconds', label: 'Sequence · duration (s)' },
    { path: 'sequence.clips', label: 'Sequence · clips' },
    ...graph.nodes.flatMap((n) => Object.keys(NODE_TYPES[n.type].outputs).map((o) => ({ path: `nodes.${n.id}.output.${o}`, label: `${n.label || NODE_TYPES[n.type].label} · ${o}` }))),
  ];
}

export function NodeInspector({ node, graph, onPatch, onRemove, problems, disabled, definitionId }: {
  node: AutomationNode; graph: AutomationGraph; onPatch: Patch; onRemove: () => void; problems: string[]; disabled: boolean;
  /** The workflow, for the Date in metadata trigger's preview (1.0.0-rc.10). */
  definitionId?: string;
}) {
  const schema = useSchema().data?.definition;
  const fields = schema?.fields ?? [];
  const storage = useStorage().data?.items.filter((l) => l.kind !== 'agent') ?? [];
  // Delete can remove copies anywhere PRISM keeps files, including folders on storage agents.
  const allLocations = useStorage().data?.items;
  const sequences = useSequences().data?.items ?? [];
  const destinations = useExportDestinations(node.type === 'export_sequence' || node.type === 'export_asset').data?.items ?? [];
  // 1.2.0: master variants, for the master pickers, the Master added trigger and has master conditions.
  const variants = useMasterVariants().data?.items ?? [];
  const endpoints = useWebhookEndpoints().data?.items.filter((e) => e.enabled) ?? [];
  const liveSources = useLiveSources().data?.items ?? [];
  const roles = useRoles().data?.items ?? [];
  const people = useAssignees().data?.people ?? [];
  const transcoder = useTranscoders().data;
  const profiles = useTranscodeProfiles().data;
  const qcProfiles = useQcProfiles(node.type === 'run_qc').data;
  const info = NODE_TYPES[node.type];
  // Transcode steps before this one, whose output later steps can copy or delete.
  const upstream = (() => {
    const seen = new Set<string>();
    const stack = [node.id];
    while (stack.length) { const cur = stack.pop(); for (const e of graph.edges.filter((x) => x.to === cur)) if (!seen.has(e.from)) { seen.add(e.from); stack.push(e.from); } }
    return graph.nodes.filter((n) => seen.has(n.id) && (n.type === 'transcode' || n.type === 'external_transcode'));
  })();
  const fileValue = (n: { representation: string; fromNode?: string }) => (n.representation === 'output' ? `output:${n.fromNode ?? ''}` : n.representation);
  const fileSet = (v: string) => set((v.startsWith('output:') ? { representation: 'output', fromNode: v.slice(7) } : { representation: v, fromNode: undefined }) as Partial<AutomationNode>);
  const fileOptions = (
    <>
      <option value="original">Original</option><option value="proxy">Proxy</option>
      {upstream.map((u) => <option key={u.id} value={`output:${u.id}`}>Output of “{u.label || NODE_TYPES[u.type].label}”</option>)}
    </>
  );
  const paths = contextPaths(graph, fields);
  const set = (patch: Partial<AutomationNode>) => onPatch({ ...node, ...patch } as AutomationNode);
  const ctx: EditorContext = { set, fields, paths, endpoints, storage, allLocations, sequences, destinations, liveSources, roles, people, transcoder, profiles, qcProfiles, fileValue, fileSet, fileOptions, definitionId, variants, graph };
  const editor = EDITORS[node.type] as NodeEditor<AutomationNode['type']> | undefined;

  return (
    <fieldset className="inspector-pane" disabled={disabled} style={{ border: 0, padding: 0, margin: 0 }} data-testid="node-inspector">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <p className="section-label" style={{ margin: 0, flex: 1 }}>{info.label}</p>
        <span className="mono faint">{node.id}</span>
        {node.type !== 'trigger' && <button type="button" className="btn ghost sm danger" onClick={onRemove}><Trash2 size={13} /> Remove</button>}
      </div>
      <span className="help">{info.description}</span>
      {problems.map((p) => <div key={p} className="banner warning" style={{ padding: '6px 10px' }}>{p}</div>)}
      <div className="field"><label htmlFor="n-label">Label</label><input id="n-label" className="input" value={node.label ?? ''} placeholder={info.label} onChange={(e) => set({ label: e.target.value || undefined })} /></div>

      <Fragment key={node.type}>{editor?.(node as never, ctx)}</Fragment>

      {node.type !== 'trigger' && (
        <details className="variables" data-testid="variables">
          <summary>Variables for templates</summary>
          <p className="help">Use <span className="mono">{'{{path}}'}</span> in text fields; click to copy.</p>
          <ul>
            {variablePaths(graph, node, fields).map((v) => (
              <li key={v.path}><button type="button" className="tag mono" onClick={() => { void navigator.clipboard?.writeText(`{{${v.path}}}`); }} title={`Copy {{${v.path}}}`}>{`{{${v.path}}}`}</button> <span className="faint">{v.label}</span></li>
            ))}
          </ul>
        </details>
      )}

      {node.type !== 'trigger' && node.type !== 'condition' && node.type !== 'delay' && node.type !== 'repeat_until' && node.type !== 'wait_until_date' && (
        <div className="field-row">
          <div className="field"><label htmlFor="n-retries">Retries</label><input id="n-retries" className="input" type="number" min={0} max={10} value={node.retries ?? ''} placeholder={node.type === 'request_approval' ? '0' : '2'} onChange={(e) => set({ retries: e.target.value === '' ? undefined : Number(e.target.value) })} /></div>
          <div className="field"><label htmlFor="n-timeout">Timeout (s)</label><input id="n-timeout" className="input" type="number" min={1} value={node.timeoutSeconds ?? ''} placeholder="default" onChange={(e) => set({ timeoutSeconds: e.target.value === '' ? undefined : Number(e.target.value) })} /></div>
        </div>
      )}
    </fieldset>
  );
}

type NodeOf<K extends AutomationNode['type']> = Extract<AutomationNode, { type: K }>;

/** What a node type's form needs besides the node: the patch function and the lists its fields offer. */
interface EditorContext {
  set: (patch: Partial<AutomationNode>) => void;
  fields: FieldDefinition[];
  paths: Array<{ path: string; label: string }>;
  endpoints: WebhookEndpointDTO[];
  storage: StorageLocationSummary[];
  allLocations: StorageLocationSummary[] | undefined;
  sequences: SequenceSummary[];
  destinations: ExportDestinationDTO[];
  liveSources: LiveSourceDTO[];
  roles: RoleDTO[];
  people: Array<{ id: string; name: string }>;
  transcoder: TranscoderStatus | undefined;
  profiles: TranscodeProfilesDTO | undefined;
  /** QC profiles (1.0.0-rc.8), with the workspace's default. */
  qcProfiles: { items: QcProfileDTO[]; defaultProfileId: string | null } | undefined;
  /** The File choice of copy and delete steps: the original, the proxy or an earlier transcode's output. */
  fileValue: (n: { representation: string; fromNode?: string }) => string;
  fileSet: (v: string) => void;
  fileOptions: ReactNode;
  definitionId?: string;
  /** The workspace's master variants (1.2.0), in their order. */
  variants: MasterVariantDTO[];
  /** The whole graph (1.3.1: Wait for delivery picks an Assign for delivery step). */
  graph: AutomationGraph;
}
type NodeEditor<K extends AutomationNode['type']> = (node: NodeOf<K>, c: EditorContext) => ReactNode;

/** The configuration form of each node type, below the label. */
const EDITORS: { [K in AutomationNode['type']]?: NodeEditor<K> } = {
  trigger: (node, { set, fields, definitionId, variants }) => (
    <>
      <div className="field"><label htmlFor="n-trigger">Starts when</label>
        <select id="n-trigger" className="select" value={node.trigger.type} onChange={(e) => {
          const type = e.target.value as Trigger['type'];
          const trigger: Trigger = type === 'schedule' ? { type, cron: '0 6 * * 1-5', timezone: 'UTC' } : type === 'metadata_changed' ? { type, fields: [] }
            : type === 'metadata_date' ? { type, ...defaultDateRule(fields), pastDates: false, catchUpHours: 24, conditions: [] } : { type } as Trigger;
          set({ trigger } as Partial<AutomationNode>);
        }}>
          {Object.entries(TRIGGER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {node.trigger.type === 'schedule' && (
        <div className="field-row">
          <div className="field"><label htmlFor="n-cron">Cron</label><input id="n-cron" className="input mono" value={node.trigger.cron} onChange={(e) => set({ trigger: { ...node.trigger, cron: e.target.value } } as Partial<AutomationNode>)} /><span className="help">minute hour day month weekday</span></div>
          <div className="field"><label htmlFor="n-tz">Time zone</label><input id="n-tz" className="input" value={node.trigger.timezone} onChange={(e) => set({ trigger: { ...node.trigger, timezone: e.target.value } } as Partial<AutomationNode>)} /></div>
        </div>
      )}
      {node.trigger.type === 'metadata_changed' && (
        <div className="field"><label>Only when these fields change</label>
          <div className="tags">{fields.map((f) => {
            const t = node.trigger as Extract<Trigger, { type: 'metadata_changed' }>;
            const on = t.fields.includes(f.id);
            return <button type="button" key={f.id} className={`tag toggle ${on ? 'on' : ''}`} onClick={() => set({ trigger: { ...t, fields: on ? t.fields.filter((x) => x !== f.id) : [...t.fields, f.id] } } as Partial<AutomationNode>)}>{f.label}</button>;
          })}</div>
          <span className="help">None selected means any field. Changes made by this workflow never re-trigger it.</span>
        </div>
      )}
      {node.trigger.type === 'manual' && (
        <div className="field"><label htmlFor="n-subject">Started on</label>
          <select id="n-subject" className="select" value={node.trigger.subject ?? 'any'} onChange={(e) => set({ trigger: { type: 'manual', subject: e.target.value as 'any' | 'asset' | 'sequence' } } as Partial<AutomationNode>)}>
            <option value="any">An asset, a sequence, or nothing</option>
            <option value="asset">An asset (Run workflow on the asset page)</option>
            <option value="sequence">A sequence (Run workflow in the editor)</option>
          </select>
          <span className="help">People with the Run workflows permission start it from the asset page or the sequence editor.</span>
        </div>
      )}
      {node.trigger.type === 'metadata_date' && (
        <DateTrigger trigger={node.trigger} fields={fields} definitionId={definitionId} onChange={(trigger) => set({ trigger } as Partial<AutomationNode>)} />
      )}
      {node.trigger.type === 'watch_folder' && <WatchFolderTrigger trigger={node.trigger} onChange={(trigger) => set({ trigger } as Partial<AutomationNode>)} />}
      {node.trigger.type === 'master_added' && (
        <div className="field"><label htmlFor="n-master-variant">Only masters of</label>
          <select id="n-master-variant" className="select" data-testid="trigger-master-variant" value={node.trigger.variantId ?? ''}
            onChange={(e) => set({ trigger: { type: 'master_added', ...(e.target.value ? { variantId: e.target.value } : {}) } } as Partial<AutomationNode>)}>
            <option value="">Any variant</option>
            {variants.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <span className="help">
            Starts when a further master arrives on an asset: uploaded with Add master…, from a watch folder, saved by a workflow step or checked in from an NLE.
            Steps can use <span className="mono">{'{{master.variant}}'}</span>, <span className="mono">{'{{master.format}}'}</span> and <span className="mono">{'{{master.label}}'}</span>.
            A master this workflow saves itself never starts it again.
          </span>
        </div>
      )}
      {node.trigger.type === 'delivery_accepted' && <span className="help" data-testid="trigger-help-delivery">Starts when Intake accepts a media portal delivery into a placeholder (complete, or with missing items). The run is on the placeholder, which now has its media.</span>}
      {node.trigger.type === 'portal_upload_accepted' && <span className="help" data-testid="trigger-help-portal-upload">Starts when Intake accepts an open upload from the media portal, as a new asset or linked to a placeholder. The run is on that asset.</span>}
      {node.trigger.type === 'webhook' && <span className="help">Create a token on the workflow’s toolbar. Callers POST JSON, or GET with query parameters, with <code>Authorization: Bearer &lt;token&gt;</code>, and may pass an <code>assetId</code>. What they send is <code>{'{{trigger.payload.<key>}}'}</code>.</span>}
    </>
  ),

  condition: (node, { set, paths, variants }) => (
    <div className="field">
      <label>All of these are true</label>
      <Conditions all={node.all} paths={paths} variants={variants} onChange={(all) => set({ all } as Partial<AutomationNode>)} />
      <span className="help">Connect the <b>true</b> and <b>false</b> ports to different steps.</span>
    </div>
  ),

  delay: (node, { set }) => (
    <div className="field"><label htmlFor="n-delay">Wait</label>
      <Duration id="n-delay" seconds={node.seconds} onChange={(seconds) => set({ seconds } as Partial<AutomationNode>)} />
      <span className="help">The run waits durably; restarts of PRISM do not cut the wait short.</span>
    </div>
  ),

  wait_until_date: (node, { set, fields }) => (
    <>
      <DateRuleFields idPrefix="n-wait" rule={node} fields={fields} onChange={(r) => set(r as Partial<AutomationNode>)} />
      <div className="field"><label htmlFor="n-wait-missing">If the date is empty or already passed</label>
        <select id="n-wait-missing" className="select" data-testid="wait-if-missing" value={node.ifMissing} onChange={(e) => set({ ifMissing: e.target.value } as Partial<AutomationNode>)}>
          <option value="continue">Continue at once</option>
          <option value="fail">Fail the run</option>
        </select>
        <span className="help">The run waits durably (restarts of PRISM do not cut it short). When it wakes it reads the date again: if someone changed it, it waits for the new time.</span>
      </div>
    </>
  ),

  create_task: (node, { set, fields }) => (
    <>
      <div className="field"><label htmlFor="n-task-assignee">Assign to</label>
        <TaskAssignee id="n-task-assignee" value={node.assignee} onChange={(assignee) => set({ assignee } as Partial<AutomationNode>)} />
        <span className="help">Every member of a role or group sees the task under Assigned to me and is notified, as for any task; one of them can take it.</span>
      </div>
      <div className="field"><label htmlFor="n-task-title">Title</label>
        <input id="n-task-title" className="input" data-testid="task-title" value={node.title} maxLength={500} onChange={(e) => set({ title: e.target.value } as Partial<AutomationNode>)} />
      </div>
      <div className="field"><label htmlFor="n-task-details">Details</label>
        <textarea id="n-task-details" className="textarea" rows={3} value={node.details} onChange={(e) => set({ details: e.target.value } as Partial<AutomationNode>)} />
        <span className="help">Templates work in both, e.g. <span className="mono">{'Promo for {{asset.title}} airs {{asset.metadata.f_air_date}}'}</span> or an earlier step’s output.</span>
      </div>
      <div className="field"><label htmlFor="n-task-due">Due</label>
        <select id="n-task-due" className="select" data-testid="task-due" value={node.due.type} onChange={(e) => {
          const type = e.target.value;
          set({ due: type === 'none' ? { type } : type === 'now' ? { type, days: 1, hours: 0 } : { type, ...defaultDateRule(fields) } } as Partial<AutomationNode>);
        }}>
          <option value="none">No due time</option>
          <option value="now">After the step runs</option>
          <option value="field">Relative to a date field</option>
        </select>
      </div>
      {node.due.type === 'now' && (() => {
        const due = node.due;
        return (
          <div className="field-row">
            <div className="field"><label htmlFor="n-task-days">Days</label><input id="n-task-days" className="input" type="number" min={0} max={365} value={due.days} onChange={(e) => set({ due: { ...due, days: Math.max(0, Number(e.target.value) || 0) } } as Partial<AutomationNode>)} /></div>
            <div className="field"><label htmlFor="n-task-hours">Hours</label><input id="n-task-hours" className="input" type="number" min={0} value={due.hours} onChange={(e) => set({ due: { ...due, hours: Math.max(0, Number(e.target.value) || 0) } } as Partial<AutomationNode>)} /></div>
          </div>
        );
      })()}
      {node.due.type === 'field' && (() => {
        const due = node.due;
        return <DateRuleFields idPrefix="n-task-due" rule={due} fields={fields} onChange={(r) => set({ due: { ...due, ...r } } as Partial<AutomationNode>)} />;
      })()}
      <span className="help">The task is linked to the run’s asset (or its sequence). Later steps can use <span className="mono">{`{{nodes.${node.id}.output.taskId}}`}</span>.</span>
    </>
  ),

  repeat_until: (node, { set, paths, endpoints, variants, graph }) => (
    <>
      <div className="field"><label htmlFor="n-check">Each time</label>
        <select id="n-check" className="select" value={node.check.type} onChange={(e) => {
          const type = e.target.value as 'asset' | 'webhook' | 'wait' | 'delivery_accepted' | 'site_service';
          set({ check: type === 'webhook' ? { type, endpointId: endpoints[0]?.id ?? '00000000-0000-4000-8000-000000000000', path: '', payload: {} } : type === 'site_service' ? { type, ...siteServiceStepDefaults() } : { type } } as Partial<AutomationNode>);
        }}>
          <option value="asset">Read the asset again</option>
          <option value="webhook">Call an endpoint (e.g. GET a status)</option>
          <option value="wait">Only wait</option>
          {/* 1.3.1 (Q4, portal-steps.tsx). */}
          <option value="delivery_accepted">Read the media portal delivery</option>
          {/* 1.6.0 (W, site-service-step.tsx). */}
          <option value="site_service">Ask a site service (through a storage agent)</option>
        </select>
      </div>
      {node.check.type === 'delivery_accepted' && <DeliveryCheckFields node={node} graph={graph} set={set} />}
      {node.check.type === 'site_service' && <SiteServiceCheckFields check={node.check} onChange={(check) => set({ check } as Partial<AutomationNode>)} />}
      {node.check.type === 'webhook' && (
        <div className="field-row">
          <div className="field"><label htmlFor="n-rep-ep">Endpoint</label>
            <select id="n-rep-ep" className="select" value={node.check.endpointId} onChange={(e) => set({ check: { ...node.check, endpointId: e.target.value } } as Partial<AutomationNode>)}>
              <option value="00000000-0000-4000-8000-000000000000">Choose…</option>
              {endpoints.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.method}</option>)}
            </select></div>
          <div className="field"><label htmlFor="n-rep-path">Path</label>
            <input id="n-rep-path" className="input mono" value={node.check.path} placeholder="jobs/{{trigger.payload.jobId}}" onChange={(e) => set({ check: { ...node.check, path: e.target.value } } as Partial<AutomationNode>)} /></div>
        </div>
      )}
      <div className="field-row">
        <div className="field"><label htmlFor="n-every">Every</label><Duration id="n-every" seconds={node.everySeconds} min={5} onChange={(everySeconds) => set({ everySeconds } as Partial<AutomationNode>)} /></div>
        <div className="field"><label htmlFor="n-max">At most (times)</label><input id="n-max" className="input" type="number" min={1} max={1000} value={node.maxAttempts} onChange={(e) => set({ maxAttempts: Math.max(1, Number(e.target.value) || 1) } as Partial<AutomationNode>)} /></div>
      </div>
      <div className="field"><label>Until all of these are true</label>
        <Conditions all={node.all} variants={variants} paths={[...paths,
          ...(node.check.type === 'delivery_accepted'
            // 1.3.1: the delivery check's answer (DeliveryCheckResponse); Wait for delivery's condition.
            ? [{ path: `nodes.${node.id}.output.response.state`, label: 'This step · delivery state (accepted, cancelled…)' }, { path: `nodes.${node.id}.output.response.accepted`, label: 'This step · delivery accepted (true or false)' }]
            : [{ path: `nodes.${node.id}.output.response`, label: 'This step · endpoint answer (add .field)' }]),
          { path: `nodes.${node.id}.output.attempts`, label: 'This step · attempts so far' }]} onChange={(all) => set({ all } as Partial<AutomationNode>)} />
        <span className="help">Leaves by <b>true</b> as soon as they hold, or by <b>false</b> after the last try. Typical: poll an external job with GET until <span className="mono">{`nodes.${node.id}.output.response.state`}</span> equals <span className="mono">done</span>.</span>
      </div>
    </>
  ),

  // 1.3.1 (Q4, portal-steps.tsx).
  assign_for_delivery: (node, { set }) => <AssignForDeliveryEditor node={node} set={set} />,
  // 1.5.0 (detect-shots-step.tsx).
  detect_shots: (node, { set }) => <DetectShotsEditor node={node} set={set} />,
  // 1.6.0 (site-service-step.tsx, W; ai-steps.tsx, A).
  call_site_service: (node, { set }) => <SiteServiceEditor node={node} set={set} />,
  for_each_shot: (node, { set }) => <ForEachEditor node={node} set={set} />,
  for_each_segment: (node, { set }) => <ForEachEditor node={node} set={set} />,
  update_segment: (node, { set }) => <UpdateSegmentEditor node={node} set={set} />,

  map_metadata: (node, { set, fields }) => (
    <div className="field"><label>Mappings</label>
      {node.mappings.map((m, i) => {
        const update = (patch: Partial<Mapping>) => set({ mappings: node.mappings.map((x, j) => (j === i ? { ...x, ...patch } : x)) } as Partial<AutomationNode>);
        return (
          <div key={i} className="mapping" data-testid="mapping">
            <div className="condition-row">
              <select className="select" aria-label="Field" value={m.field} onChange={(e) => update({ field: e.target.value })}>
                <option value="">Field…</option>
                {fields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
              <select className="select" aria-label="Mode" value={m.mode} onChange={(e) => update({ mode: e.target.value as Mapping['mode'] })}>
                <option value="overwrite">Overwrite</option><option value="if_empty">Only if empty</option>
              </select>
              <button type="button" className="btn ghost icon sm" aria-label="Remove mapping" disabled={node.mappings.length === 1} onClick={() => set({ mappings: node.mappings.filter((_, j) => j !== i) } as Partial<AutomationNode>)}><Trash2 size={12} /></button>
            </div>
            <input className="input mono" aria-label="From" value={m.from} placeholder="{{trigger.payload.title}}" onChange={(e) => update({ from: e.target.value })} />
            <div className="condition-row">
              <select className="select" aria-label="Transform" value={m.transform} onChange={(e) => update({ transform: e.target.value as Mapping['transform'] })}>
                {Object.entries(TRANSFORMS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <input className="input" aria-label="If empty" value={m.fallback} placeholder="If empty…" onChange={(e) => update({ fallback: e.target.value })} />
            </div>
            <input className="input mono" aria-label="Lookup" value={Object.entries(m.lookup).map(([k, v]) => `${k}=${v}`).join('; ')} placeholder="Lookup, e.g. SPORT=Sports; NEWS=News"
              onChange={(e) => update({ lookup: Object.fromEntries(e.target.value.split(';').map((p) => p.split('=')).filter((p) => p.length === 2 && p[0]!.trim()).map(([k, v]) => [k!.trim(), v!.trim()])) })} />
          </div>
        );
      })}
      <button type="button" className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => set({ mappings: [...node.mappings, { field: '', from: '', transform: 'none', lookup: {}, fallback: '', mode: 'overwrite' }] } as Partial<AutomationNode>)}><Plus size={13} /> Mapping</button>
      <span className="help">Each mapping fills one field from a template. Empty results leave the field alone unless “If empty” gives a value. Lookups replace whole values before the transform.</span>
    </div>
  ),

  validate_metadata: (node, { set, fields }) => (
    <div className="field"><label>Required fields</label>
      <div className="tags">{fields.map((f) => {
        const on = node.required.includes(f.id);
        return <button type="button" key={f.id} className={`tag toggle ${on ? 'on' : ''}`} onClick={() => { if (on && node.required.length === 1) return; set({ required: on ? node.required.filter((x) => x !== f.id) : [...node.required, f.id] } as Partial<AutomationNode>); }}>{f.label}</button>;
      })}</div>
    </div>
  ),

  update_metadata: (node, { set, fields }) => (
    <div className="field"><label>Set fields</label>
      {Object.entries(node.values).map(([k, v]) => (
        <div className="condition-row" key={k}>
          <span className="readonly-value" style={{ minWidth: 120 }}>{fields.find((f) => f.id === k)?.label ?? k}</span>
          <input className="input" value={v} aria-label={`Value for ${k}`} onChange={(e) => set({ values: { ...node.values, [k]: e.target.value } } as Partial<AutomationNode>)} />
          <button type="button" className="btn ghost icon sm" aria-label="Remove field" onClick={() => { const next = { ...node.values }; delete next[k]; set({ values: next } as Partial<AutomationNode>); }}><Trash2 size={12} /></button>
        </div>
      ))}
      <select className="select" value="" aria-label="Add a field" onChange={(e) => e.target.value && set({ values: { ...node.values, [e.target.value]: '' } } as Partial<AutomationNode>)}>
        <option value="">Add a field…</option>
        {fields.filter((f) => !(f.id in node.values)).map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
      </select>
      <span className="help">Use {'{{asset.title}}'} or {'{{nodes.<step>.output.<field>}}'} to insert values.</span>
      {/* 1.6.0 (A, ai-steps.tsx): write the values as suggestions to accept. */}
      <UpdateMetadataSuggestionOption node={node} set={set} />
    </div>
  ),

  export_asset: (node, { set, destinations, variants }) => (
    <>
      <div className="field-row">
        <div className="field"><label htmlFor="n-xa-preset">Format</label>
          <PresetSelect id="n-xa-preset" value={node.preset} original="" originalHelp="The master as it is, copied (or rewrapped for the programme)" onChange={(preset) => set({ preset, ...(preset === 'original' ? { quality: 'original' } : {}) } as Partial<AutomationNode>)} />
        </div>
        <div className="field"><label htmlFor="n-xa-quality">Source</label>
          <select id="n-xa-quality" className="select" data-testid="export-asset-quality" value={node.quality} disabled={node.preset === 'original'} onChange={(e) => set({ quality: e.target.value } as Partial<AutomationNode>)}>
            <option value="original">Original</option><option value="proxy">Proxy (review)</option>
          </select>
        </div>
      </div>
      {node.quality === 'original' && (
        <MasterChoiceFields id="n-xa-master" label="Master" value={node.master} variants={variants} closestLabel={node.preset === 'original' ? 'Largest' : 'Closest to the format'}
          onChange={(master) => set({ master } as Partial<AutomationNode>)} testId="export-asset-master"
          help={node.preset === 'original' ? 'The Original format delivers the chosen master’s own file.' : 'A variant’s master closest to the format (no upscaling where a larger one exists), or a fixed format. Default: Programme.'} />
      )}
      <div className="field-row">
        <div className="field"><label htmlFor="n-xa-range">Range</label>
          <select id="n-xa-range" className="select" data-testid="export-asset-range" value={node.range} onChange={(e) => set({ range: e.target.value } as Partial<AutomationNode>)}>
            <option value="full">The whole asset</option><option value="programme">The programme (in to out)</option>
          </select>
        </div>
        <div className="field"><label htmlFor="n-xa-subs">Subtitles</label>
          <select id="n-xa-subs" className="select" data-testid="export-asset-subtitles" value={node.subtitles.mode} onChange={(e) => set({ subtitles: { ...node.subtitles, mode: e.target.value } } as Partial<AutomationNode>)}>
            <option value="none">None</option>
            <option value="burn" disabled={node.preset === 'original'}>Burnt in</option>
            <option value="sidecar">Sidecar files</option>
            <option value="both" disabled={node.preset === 'original'}>Both</option>
          </select>
        </div>
      </div>
      {node.subtitles.mode !== 'none' && (
        <div className="field"><label htmlFor="n-xa-lang">Subtitle language</label>
          <input id="n-xa-lang" className="input" data-testid="export-asset-language" value={node.subtitles.language ?? ''} placeholder="The asset’s first track"
            onChange={(e) => set({ subtitles: { ...node.subtitles, language: e.target.value.trim() || null } } as Partial<AutomationNode>)} />
          <span className="help">A language code such as fi or en-GB; the asset’s first track in it is used. Empty: its first track.</span>
        </div>
      )}
      {(node.subtitles.mode === 'sidecar' || node.subtitles.mode === 'both') && (
        <SidecarFormats value={node.subtitles.sidecarFormats ?? DEFAULT_SIDECAR_FORMATS} testId="export-asset-sidecars"
          onChange={(f) => { const { sidecarFormats: _old, ...rest } = node.subtitles; void _old; set({ subtitles: { ...rest, ...(f.length === 2 && f.includes('srt') && f.includes('vtt') ? {} : { sidecarFormats: f }) } } as Partial<AutomationNode>); }} />
      )}
      <div className="field"><label htmlFor="n-xa-dest">Export to</label>
        <DestinationSelect id="n-xa-dest" value={node.destinationLocationId ?? ''} onChange={(v) => set({ destinationLocationId: v || undefined } as Partial<AutomationNode>)} destinations={destinations} />
      </div>
      <label className="toggle-row">
        <input type="checkbox" checked={node.restore} data-testid="export-asset-restore" onChange={(e) => set({ restore: e.target.checked } as Partial<AutomationNode>)} />
        <span><strong>Restore archived media first</strong><span className="help" style={{ display: 'block' }}>The export waits for the restore; without this, a master only in an archive fails the step.</span></span>
      </label>
      <span className="help">The step waits for the export. Later steps can use <span className="mono">{`{{nodes.${node.id}.output.exportId}}`}</span> and <span className="mono">{`{{nodes.${node.id}.output.key}}`}</span>, for example in a Call webhook step. Flow exports do not count toward the 20 batch exports in progress.</span>
    </>
  ),

  transcode: (node, { set, profiles, variants }) => (
    <>
    <div className="field"><label htmlFor="n-preset">Transcode profile</label>
      <select id="n-preset" className="select" value={node.preset} onChange={(e) => set({ preset: e.target.value } as Partial<AutomationNode>)} data-testid="transcode-profile">
        {(profiles?.builtin ?? [{ id: 'h264-1080p', name: 'H.264 1080p (built-in)' }, { id: 'h264-720p', name: 'H.264 720p (built-in)' }]).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        {profiles?.items.filter((p) => p.purpose === 'rendition').map((p) => <option key={p.id} value={p.id}>{p.name} (.{p.extension})</option>)}
        {profiles && ![...profiles.builtin.map((b) => b.id), ...profiles.items.map((p) => p.id)].includes(node.preset) && <option value={node.preset}>Removed profile</option>}
      </select>
      <span className="help">
        {node.saveAs?.type === 'master' ? 'The result is saved as a master of the asset (below).' : 'The result is kept as a rendition of the asset.'} Later steps use it as <b>“Output of {node.label || 'Transcode'}”</b> in Copy to storage and Delete,
        and templates can insert <span className="mono">{`{{nodes.${node.id}.output.filename}}`}</span> or <span className="mono">{`{{nodes.${node.id}.output.representationId}}`}</span>.
        Add or edit profiles in <Link to="/admin/transcoding">Administration → Transcoding</Link>. Originals on a storage agent are fetched for the step and removed afterwards.
      </span>
    </div>
      {/* 1.2.0: which master is read, and Save result as master. */}
      <MasterChoiceFields id="n-source" label="Source" value={node.source} variants={variants} closestLabel="Closest to the profile"
        onChange={(source) => set({ source } as Partial<AutomationNode>)} testId="node-source"
        help="Which master is transcoded: the primary, or a variant in the format closest to the profile’s output (the smallest at least as large), or in a fixed format." />
      <SaveAsFields idPrefix="n-transcode" value={node.saveAs} variants={variants} onChange={(saveAs) => set({ saveAs } as Partial<AutomationNode>)} />
    </>
  ),

  external_transcode: (node, { set, transcoder, variants }) => (
    <>
      {!transcoder || !transcoder.configured ? (
        <div className="banner warning" style={{ padding: '6px 10px' }}>No external transcoder is configured for this installation.</div>
      ) : !transcoder.reachable ? (
        <div className="banner error" style={{ padding: '6px 10px' }}>The transcoder is not reachable: {transcoder.error}</div>
      ) : (
        <>
          <div className={`banner ${transcoder.info.simulated ? 'warning' : 'info'}`} style={{ padding: '6px 10px' }} data-testid="transcoder-engine">
            {transcoder.info.simulated ? <><b>Simulator.</b> Jobs run on PRISM’s built-in stand-in transcoder, not a real product.</> : <>{transcoder.info.product} {transcoder.info.version}</>}
          </div>
          <div className="field"><label htmlFor="n-prof">Profile</label>
            <select id="n-prof" className="select" value={node.profileId} onChange={(e) => set({ profileId: e.target.value } as Partial<AutomationNode>)}>
              <option value="">Choose…</option>
              {transcoder.info.profiles.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <span className="help">{transcoder.info.profiles.find((p) => p.id === node.profileId)?.description ?? 'The output is stored as a rendition of the asset, with the external job id recorded.'}</span>
          </div>
        </>
      )}
      <div className="field"><label>Labels passed to the transcoder</label>
        {Object.entries(node.labels).map(([k, v]) => (
          <div className="condition-row" key={k}>
            <span className="readonly-value mono" style={{ minWidth: 100 }}>{k}</span>
            <input className="input" value={v} aria-label={`Label ${k}`} onChange={(e) => set({ labels: { ...node.labels, [k]: e.target.value } } as Partial<AutomationNode>)} />
            <button type="button" className="btn ghost icon sm" aria-label="Remove label" onClick={() => { const next = { ...node.labels }; delete next[k]; set({ labels: next } as Partial<AutomationNode>); }}><Trash2 size={12} /></button>
          </div>
        ))}
        <button type="button" className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => { const k = window.prompt('Label name'); if (k) set({ labels: { ...node.labels, [k]: '' } } as Partial<AutomationNode>); }}><Plus size={13} /> Label</button>
      </div>
      {/* 1.2.0: which master the transcoder reads, and Save result as master. */}
      <MasterChoiceFields id="n-ext-source" label="Source" value={node.source} variants={variants} closestLabel="Largest"
        onChange={(source) => set({ source } as Partial<AutomationNode>)} testId="node-source"
        help="Which master the transcoder reads: the primary, or a variant (its largest master, or a fixed format)." />
      <SaveAsFields idPrefix="n-ext" value={node.saveAs} variants={variants} onChange={(saveAs) => set({ saveAs } as Partial<AutomationNode>)} />
    </>
  ),

  run_qc: (node, { set, qcProfiles, fileOptions, variants }) => {
    const def = qcProfiles?.items.find((p) => p.id === qcProfiles.defaultProfileId);
    const value = node.source === 'output' ? `output:${node.fromNode ?? ''}` : node.source;
    return (
      <>
        <div className="field"><label htmlFor="n-qc-profile">Profile</label>
          <select id="n-qc-profile" className="select" data-testid="qc-node-profile" value={node.profileId ?? ''} onChange={(e) => set({ profileId: e.target.value || undefined } as Partial<AutomationNode>)}>
            <option value="">Default{def ? ` (${def.name})` : ''}</option>
            {qcProfiles?.items.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <span className="help">Profiles are set up in Administration → Quality control. <Link to="/help/quality-control#workflows">How QC works in workflows</Link></span>
        </div>
        <div className="field"><label htmlFor="n-qc-source">Check</label>
          <select id="n-qc-source" className="select" data-testid="qc-node-source" value={value}
            onChange={(e) => set((e.target.value.startsWith('output:') ? { source: 'output', fromNode: e.target.value.slice(7) } : { source: e.target.value, fromNode: undefined }) as Partial<AutomationNode>)}>
            {fileOptions}
          </select>
        </div>
        {node.source === 'original' && (
          <MasterChoiceFields id="n-qc-master" label="Master" value={node.master} variants={variants} closestLabel="Largest" testId="qc-node-master"
            onChange={(master) => set({ master } as Partial<AutomationNode>)}
            help="Which master is checked: the primary (the asset’s QC result follows it), or a variant’s. A master’s QC run shows on the asset’s QC tab." />
        )}
        <span className="help">Continues by <b>passed</b>, <b>warnings</b> or <b>failed</b>. Later steps can use its status, errors, warnings, loudnessLufs and truePeakDbtp, for example to write the loudness into a field. A workflow runs QC every time; only Run QC… on an asset is limited to once per version.</span>
      </>
    );
  },
  copy_to_storage: (node, { set, storage, fileValue, fileSet, fileOptions, variants }) => (
    <>
      <div className="field-row">
        <div className="field"><label htmlFor="n-loc">Destination</label>
          <select id="n-loc" className="select" value={node.locationId} onChange={(e) => set({ locationId: e.target.value } as Partial<AutomationNode>)}>
            <option value="00000000-0000-4000-8000-000000000000">Choose…</option>
            {storage.map((l) => <option key={l.id} value={l.id}>{l.name} · {l.site}</option>)}
          </select>
        </div>
        <div className="field"><label htmlFor="n-rep">File</label>
          <select id="n-rep" className="select" value={fileValue(node)} onChange={(e) => fileSet(e.target.value)}>{fileOptions}</select>
        </div>
      </div>
      {node.representation === 'original' && (
        <MasterChoiceFields id="n-copy-master" label="Master" value={node.master} variants={variants} allowAll closestLabel="Largest" testId="copy-node-master"
          onChange={(master) => set({ master } as Partial<AutomationNode>)}
          help="The primary master, a variant’s (its largest, or a fixed format), or every master of the asset, each copied and verified." />
      )}
    </>
  ),

  delete: (node, { set, allLocations, fileValue, fileSet, fileOptions, variants }) => (
    <>
      <div className="field"><label>Delete</label>
        <div className="segmented" role="radiogroup">
          <button type="button" aria-pressed={node.target === 'copy'} onClick={() => set({ target: 'copy' } as Partial<AutomationNode>)}>A copy of a file</button>
          <button type="button" aria-pressed={node.target === 'asset'} onClick={() => set({ target: 'asset' } as Partial<AutomationNode>)} data-testid="delete-asset">The asset</button>
        </div>
        <span className="help">{node.target === 'asset' ? 'Moves the asset to the recycle bin, as a person deleting it would.' : 'Deletes the file from one storage location and forgets that copy.'}</span>
      </div>
      {node.target === 'copy' && (
        <>
          <div className="field-row">
            <div className="field"><label htmlFor="n-del-rep">File</label>
              <select id="n-del-rep" className="select" value={fileValue(node)} onChange={(e) => fileSet(e.target.value)}>{fileOptions}</select>
            </div>
            <div className="field"><label htmlFor="n-del-loc">From</label>
              <select id="n-del-loc" className="select" value={node.locationId ?? ''} onChange={(e) => set({ locationId: e.target.value || undefined } as Partial<AutomationNode>)} data-testid="delete-location">
                <option value="">Choose…</option>
                {(allLocations ?? []).map((l) => <option key={l.id} value={l.id}>{l.name} · {l.site}</option>)}
              </select>
            </div>
          </div>
          {node.representation === 'original' && (
            <MasterChoiceFields id="n-del-master" label="Master" value={node.master} variants={variants} allowAll closestLabel="Largest" testId="delete-node-master"
              onChange={(master) => set({ master } as Partial<AutomationNode>)}
              help="Whose copy is deleted there: the primary master’s, a variant’s, or every master’s." />
          )}
          <label className="toggle-row">
            <input type="checkbox" checked={node.onlyCopy === 'fail'} onChange={(e) => set({ onlyCopy: e.target.checked ? 'fail' : 'allow' } as Partial<AutomationNode>)} data-testid="delete-only-copy" />
            <span><strong>Fail if it is the only copy</strong><span className="help" style={{ display: 'block' }}>The step fails instead of deleting the last copy of the file anywhere.</span></span>
          </label>
        </>
      )}
    </>
  ),

  export_edl: (node, { set }) => (
    <div className="field"><label htmlFor="n-edl-title">EDL title</label>
      <input id="n-edl-title" className="input mono" value={node.title} onChange={(e) => set({ title: e.target.value } as Partial<AutomationNode>)} />
      <span className="help">A CMX 3600 EDL of the run’s sequence, saved with the exports. Templates such as {'{{sequence.name}}'} work here.</span>
    </div>
  ),

  export_sequence: (node, { set, sequences, destinations }) => (
    <>
      <div className="field"><label htmlFor="n-seq">Sequence</label>
        <select id="n-seq" className="select" value={node.sequenceId ?? ''} onChange={(e) => set({ sequenceId: e.target.value || undefined } as Partial<AutomationNode>)}>
          <option value="">The sequence the run is started on</option>
          {sequences.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div className="field-row">
        <div className="field"><label htmlFor="n-epre">Format</label><PresetSelect id="n-epre" value={node.preset} onChange={(preset) => set({ preset } as Partial<AutomationNode>)} /></div>
        <div className="field"><label htmlFor="n-eq">Source quality</label><select id="n-eq" className="select" value={node.quality} onChange={(e) => set({ quality: e.target.value } as Partial<AutomationNode>)}><option value="original">Originals</option><option value="proxy">Proxies (review)</option></select></div>
      </div>
      <div className="field"><label htmlFor="n-edest">Export to</label>
        <DestinationSelect id="n-edest" value={node.destinationLocationId ?? ''} onChange={(v) => set({ destinationLocationId: v || undefined } as Partial<AutomationNode>)} destinations={destinations} />
      </div>
    </>
  ),

  request_approval: (node, { set, roles, people }) => (
    <>
      <div className="field"><label htmlFor="n-role">Who approves</label>
        {/* Anyone with a role, or (0.31.0) one person; the approval appears in their Tasks. */}
        <select id="n-role" className="select" data-testid="approval-approver" value={node.userId ? `user:${node.userId}` : `role:${node.role ?? ''}`}
          onChange={(e) => {
            const v = e.target.value;
            set((v.startsWith('user:') ? { role: undefined, userId: v.slice(5) } : { role: v.slice(5), userId: undefined }) as Partial<AutomationNode>);
          }}>
          <optgroup label="Anyone with the role">
            {node.role && !roles.some((r) => r.key === node.role) && <option value={`role:${node.role}`}>{node.role}</option>}
            {roles.map((r) => <option key={r.key} value={`role:${r.key}`}>{r.name}</option>)}
          </optgroup>
          <optgroup label="One person">
            {node.userId && !people.some((p) => p.id === node.userId) && <option value={`user:${node.userId}`}>Someone no longer here</option>}
            {people.map((p) => <option key={p.id} value={`user:${p.id}`}>{p.name}</option>)}
          </optgroup>
        </select>
      </div>
      <div className="field"><label htmlFor="n-ins">Instructions</label><textarea id="n-ins" className="textarea" rows={3} value={node.instructions} onChange={(e) => set({ instructions: e.target.value } as Partial<AutomationNode>)} /></div>
      <span className="help">The run waits durably (up to the timeout, 14 days by default). The request appears in Tasks → Approvals. Connect <b>approved</b> and <b>rejected</b>.</span>
    </>
  ),

  live_source: (node, { set, liveSources }) => (
    <>
      <div className="field"><label htmlFor="n-ls">Live source</label>
        <select id="n-ls" className="select" value={node.sourceId} onChange={(e) => set({ sourceId: e.target.value } as Partial<AutomationNode>)} data-testid="node-live-source">
          <option value="00000000-0000-4000-8000-000000000000">Choose…</option>
          {liveSources.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.protocol.toUpperCase()} {s.port}</option>)}
        </select>
      </div>
      <div className="field"><label>Action</label>
        <div className="segmented">
          <button type="button" aria-pressed={node.action === 'start'} onClick={() => set({ action: 'start' } as Partial<AutomationNode>)}>Start</button>
          <button type="button" aria-pressed={node.action === 'stop'} onClick={() => set({ action: 'stop' } as Partial<AutomationNode>)}>Stop</button>
        </div>
        <span className="help">To record a programme at set times: a schedule trigger, <b>Start</b>, a Delay as long as the programme, then <b>Stop</b>. Sources are set up under <Link to="/admin/live">Administration → Live sources</Link>.</span>
      </div>
    </>
  ),

  call_webhook: (node, { set, endpoints }) => (
    <>
      <div className="field"><label htmlFor="n-ep">Endpoint</label>
        <select id="n-ep" className="select" value={node.endpointId} onChange={(e) => set({ endpointId: e.target.value } as Partial<AutomationNode>)}>
          <option value="00000000-0000-4000-8000-000000000000">Choose…</option>
          {endpoints.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.url}</option>)}
        </select>
        <span className="help">Administrators add endpoints under <Link to="/admin/webhooks">Administration → Webhooks</Link>. Requests are signed (X-Prism-Signature) and carry an Idempotency-Key.</span>
      </div>
      <div className="field"><label htmlFor="n-ep-path">Path</label>
        <input id="n-ep-path" className="input mono" value={node.path ?? ''} placeholder="items/{{asset.metadata.f_house_id}}" onChange={(e) => set({ path: e.target.value } as Partial<AutomationNode>)} />
        <span className="help">Appended to the endpoint’s URL. With GET the payload becomes query parameters. A JSON answer is <span className="mono">{`{{nodes.${node.id}.output.response…}}`}</span> for later steps, e.g. a Map metadata step.</span>
      </div>
      <div className="field"><label>Payload</label>
        {Object.entries(node.payload).map(([k, v]) => (
          <div className="condition-row" key={k}>
            <span className="readonly-value mono" style={{ minWidth: 100 }}>{k}</span>
            <input className="input" value={v} aria-label={`Payload ${k}`} onChange={(e) => set({ payload: { ...node.payload, [k]: e.target.value } } as Partial<AutomationNode>)} />
            <button type="button" className="btn ghost icon sm" aria-label="Remove key" onClick={() => { const next = { ...node.payload }; delete next[k]; set({ payload: next } as Partial<AutomationNode>); }}><Trash2 size={12} /></button>
          </div>
        ))}
        <button type="button" className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => { const k = window.prompt('Key name'); if (k) set({ payload: { ...node.payload, [k]: '' } } as Partial<AutomationNode>); }}><Plus size={13} /> Key</button>
      </div>
      {/* 1.6.0 (A, ai-steps.tsx): a JSON body, the time to answer, media links. */}
      <CallWebhookJsonOptions node={node} set={set} endpoints={endpoints} />
    </>
  ),
};

const TRANSFORMS: Record<Mapping['transform'], string> = {
  none: 'As is', trim: 'Trim spaces', upper: 'UPPER CASE', lower: 'lower case', capitalize: 'Capitalize Words', number: 'Number', date: 'Date (YYYY-MM-DD)', first_line: 'First line only',
};

/** Variables a step can use: the trigger, the asset, the sequence, and outputs of steps before it. */
function variablePaths(graph: AutomationGraph, node: AutomationNode, fields: Array<{ id: string; label: string }>): Array<{ path: string; label: string }> {
  const seen = new Set<string>();
  const stack = [node.id];
  while (stack.length) { const cur = stack.pop(); for (const e of graph.edges.filter((x) => x.to === cur)) if (!seen.has(e.from)) { seen.add(e.from); stack.push(e.from); } }
  const before = graph.nodes.filter((n) => seen.has(n.id) && n.type !== 'trigger');
  return [
    { path: 'asset.title', label: 'Asset title' }, { path: 'asset.id', label: 'Asset id' },
    ...fields.map((f) => ({ path: `asset.metadata.${f.id}`, label: f.label })),
    { path: 'asset.mediaKind', label: 'video, audio or image' }, { path: 'asset.durationSeconds', label: 'Duration in seconds' },
    { path: 'asset.width', label: 'Width' }, { path: 'asset.height', label: 'Height' }, { path: 'asset.frameRate', label: 'Frame rate' },
    { path: 'sequence.name', label: 'Sequence name' }, { path: 'trigger.type', label: 'What started the run' }, { path: 'trigger.actor', label: 'Who started it' },
    { path: 'trigger.payload.<key>', label: 'A value a webhook caller sent' },
    { path: 'asset.className', label: 'Asset class' }, { path: 'asset.qcStatus', label: 'QC result of the current version' },
    // 1.2.0: masters.
    { path: 'asset.masters', label: 'The asset’s masters (their labels, comma separated)' },
    ...(masterAdded(graph)
      ? [
          { path: 'master.variant', label: 'The new master’s variant (Textless…)' }, { path: 'master.format', label: 'Its format (UHD 2160p25 · ProRes 422 HQ…)' },
          { path: 'master.label', label: 'Its label (variant and format)' }, { path: 'master.id', label: 'Its id' },
        ]
      : []),
    ...(graph.nodes.some((n) => n.type === 'trigger' && n.trigger.type === 'metadata_date')
      ? [{ path: 'trigger.fieldId', label: 'The date field that came due' }, { path: 'trigger.date', label: 'Its value' }, { path: 'trigger.firingAt', label: 'When the trigger fired (UTC)' }]
      : []),
    ...before.flatMap((n) => Object.entries(NODE_TYPES[n.type].outputs).map(([o, t]) => ({ path: `nodes.${n.id}.output.${o}${t === 'object' ? '.<key>' : ''}`, label: `${n.label || NODE_TYPES[n.type].label} · ${o}` }))),
    // 1.6.0 (A, ai-steps.tsx): {{item.*}} inside a For each body.
    ...forEachItemPaths(graph, node),
  ];
}

/** A list of conditions that must all hold. */
const OPERATORS: Array<{ op: Condition['op']; label: string }> = [
  { op: 'equals', label: 'equals' }, { op: 'not_equals', label: 'not equals' }, { op: 'contains', label: 'contains' },
  { op: 'greater_than', label: 'greater than' }, { op: 'less_than', label: 'less than' }, { op: 'exists', label: 'exists' }, { op: 'not_exists', label: 'not exists' },
];
/** 1.2.0: "Asset · masters" offers has master / no master, with a variant and a format. */
const MASTER_OPERATORS: Array<{ op: Condition['op']; label: string }> = [{ op: 'has_master', label: 'has master' }, { op: 'no_master', label: 'has no master' }];
const isMasterOp = (op: Condition['op']) => op === 'has_master' || op === 'no_master';

function Conditions({ all, paths, onChange, allowEmpty = false, variants = [] }: {
  all: Condition[]; paths: Array<{ path: string; label: string }>; onChange: (all: Condition[]) => void; allowEmpty?: boolean; variants?: MasterVariantDTO[];
}) {
  return (
    <>
      {all.map((c, i) => {
        const update = (patch: Partial<Condition>) => onChange(all.map((x, j) => (j === i ? { ...x, ...patch } : x)));
        const custom = !paths.some((p) => p.path === c.path);
        const masters = c.path === 'asset.masters';
        // The new master's variant (Master added): compared with a variant's id, chosen by name.
        const variantValue = c.path === 'trigger.payload.variantId' && (c.op === 'equals' || c.op === 'not_equals');
        const setPath = (path: string) => {
          if (path === 'asset.masters') update({ path, op: 'has_master', master: { variantId: null, format: null }, value: undefined });
          else update({ path, ...(isMasterOp(c.op) ? { op: 'equals' as const, master: undefined } : {}) });
        };
        return (
          <div className="condition-row cond" key={i} data-testid="condition">
            <select className="select" aria-label="Value" value={custom ? '__custom' : c.path} onChange={(e) => setPath(e.target.value === '__custom' ? c.path : e.target.value)}>
              {paths.map((p) => <option key={p.path} value={p.path}>{p.label}</option>)}
              <option value="__custom">Another path…</option>
            </select>
            {custom && <input className="input mono" aria-label="Path" value={c.path} onChange={(e) => update({ path: e.target.value })} />}
            <select className="select" aria-label="Operator" value={c.op} onChange={(e) => {
              const op = e.target.value as Condition['op'];
              update(isMasterOp(op) ? { op, master: c.master ?? { variantId: null, format: null }, value: undefined } : { op, master: undefined });
            }}>
              {(masters ? MASTER_OPERATORS : [...OPERATORS, ...(isMasterOp(c.op) ? MASTER_OPERATORS : [])]).map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
            </select>
            {isMasterOp(c.op) ? (
              <>
                <select className="select" aria-label="Master variant" data-testid="condition-master-variant" value={c.master?.variantId ?? ''}
                  onChange={(e) => update({ master: { variantId: e.target.value || null, format: c.master?.format ?? null } })}>
                  <option value="">Any variant</option>
                  {variants.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                <select className="select" aria-label="Master format" data-testid="condition-master-format" value={c.master?.format ?? ''}
                  onChange={(e) => update({ master: { variantId: c.master?.variantId ?? null, format: (e.target.value || null) as FormatClass | null } })}>
                  <option value="">Any format</option>
                  {FORMAT_CLASSES.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </>
            ) : variantValue ? (
              <select className="select" aria-label="Compare to" data-testid="condition-variant-value" value={String(c.value ?? '')} onChange={(e) => update({ value: e.target.value })}>
                <option value="">Choose a variant…</option>
                {variants.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            ) : !['exists', 'not_exists'].includes(c.op) && <input className="input" aria-label="Compare to" value={String(c.value ?? '')} onChange={(e) => update({ value: e.target.value })} />}
            <button type="button" className="btn ghost icon sm" aria-label="Remove condition" disabled={!allowEmpty && all.length === 1} onClick={() => onChange(all.filter((_, j) => j !== i))}><Trash2 size={12} /></button>
          </div>
        );
      })}
      <button type="button" className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => onChange([...all, { path: 'asset.mediaKind', op: 'equals', value: 'video' }])}><Plus size={13} /> Condition</button>
    </>
  );
}

/**
 * Which master a step reads or acts on (1.2.0): the primary, a variant (then the format closest to the output, or a
 * fixed format class), or (Copy to storage, Delete) every master. A workflow never names one asset's master.
 */
function MasterChoiceFields({ id, label, value, onChange, variants, allowAll = false, primaryLabel = 'Primary master', closestLabel = 'Closest to the output', help, testId = 'node-master' }: {
  id: string; label: string; value: MasterChoiceOrAll | undefined; onChange: (v: MasterChoiceOrAll) => void; variants: MasterVariantDTO[];
  allowAll?: boolean; primaryLabel?: string; closestLabel?: string; help?: ReactNode; testId?: string;
}) {
  const v = value ?? { type: 'primary' as const };
  const programme = variants.find((x) => x.key === 'programme');
  // The variant select's value: primary, all, or a variant id (Programme is stored as null).
  const which = v.type === 'variant' ? (v.variantId ?? programme?.id ?? 'programme') : v.type === 'all' ? 'all' : 'primary';
  return (
    <div className="field flow-master">
      <label htmlFor={id}>{label}</label>
      <div className="condition-row">
        <select id={id} className="select" data-testid={testId} value={which} onChange={(e) => {
          const s = e.target.value;
          if (s === 'primary' || s === 'all') onChange({ type: s });
          else onChange({ type: 'variant', variantId: s === programme?.id || s === 'programme' ? null : s, format: v.type === 'variant' ? v.format : null });
        }}>
          <option value="primary">{primaryLabel}</option>
          {variants.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          {!programme && <option value="programme">Programme</option>}
          {allowAll && <option value="all">Every master</option>}
        </select>
        {v.type === 'variant' && (
          <select className="select" aria-label={`${label}: format`} data-testid={`${testId}-format`} value={v.format ?? ''}
            onChange={(e) => onChange({ ...v, format: (e.target.value || null) as FormatClass | null })}>
            <option value="">{closestLabel}</option>
            {FORMAT_CLASSES.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        )}
      </div>
      {help && <span className="help">{help}</span>}
    </div>
  );
}

/** A transcode's result (1.2.0): a rendition, or a master of a variant (the source's by default), replacing or keeping one already there. */
function SaveAsFields({ idPrefix, value, onChange, variants }: { idPrefix: string; value: SaveAs | undefined; onChange: (v: SaveAs) => void; variants: MasterVariantDTO[] }) {
  const v = value ?? { type: 'rendition' as const };
  return (
    <>
      <div className="field"><label htmlFor={`${idPrefix}-save`}>Save result as</label>
        <select id={`${idPrefix}-save`} className="select" data-testid="node-save-as" value={v.type}
          onChange={(e) => onChange(e.target.value === 'master' ? { type: 'master', variantId: null, onExisting: 'replace' } : { type: 'rendition' })}>
          <option value="rendition">A rendition</option>
          <option value="master">A master of the asset</option>
        </select>
      </div>
      {v.type === 'master' && (
        <div className="field-row flow-save-as">
          <div className="field"><label htmlFor={`${idPrefix}-save-variant`}>Variant</label>
            <select id={`${idPrefix}-save-variant`} className="select" data-testid="node-save-variant" value={v.variantId ?? ''} onChange={(e) => onChange({ ...v, variantId: e.target.value || null })}>
              <option value="">The same as the source</option>
              {variants.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </div>
          <div className="field"><label htmlFor={`${idPrefix}-save-existing`}>If it has one already</label>
            <select id={`${idPrefix}-save-existing`} className="select" data-testid="node-save-existing" value={v.onExisting} onChange={(e) => onChange({ ...v, onExisting: e.target.value as 'replace' | 'skip' })}>
              <option value="replace">Replace it</option>
              <option value="skip">Keep it (skip saving)</option>
            </select>
          </div>
        </div>
      )}
      {v.type === 'master' && (
        <span className="help">
          The result becomes a master of the asset’s current version, its format read from the file (e.g. “HD 1080p25 · H.264 High”). A master of the same variant and format is replaced once the new one is stored, or kept.
          It must be the same content: as long as the primary to within a frame (unless the variant allows another length, as Textless does) and at a fitting frame rate; otherwise the step fails with <span className="mono">DIFFERENT_CONTENT</span>.
        </span>
      )}
    </>
  );
}

/** Seconds entered as a number and a unit. */
function Duration({ id, seconds, min = 1, onChange }: { id: string; seconds: number; min?: number; onChange: (s: number) => void }) {
  const unit = seconds % 86400 === 0 ? 86400 : seconds % 3600 === 0 ? 3600 : seconds % 60 === 0 ? 60 : 1;
  return (
    <div className="condition-row">
      <input id={id} className="input" type="number" min={1} value={seconds / unit} onChange={(e) => onChange(Math.max(min, Math.round((Number(e.target.value) || 0) * unit)))} />
      <select className="select" aria-label="Unit" value={unit} onChange={(e) => onChange(Math.max(min, Math.round((seconds / unit) * Number(e.target.value))))}>
        <option value={1}>seconds</option><option value={60}>minutes</option><option value={3600}>hours</option><option value={86400}>days</option>
      </select>
    </div>
  );
}

function WatchFolderTrigger({ trigger, onChange }: { trigger: Extract<Trigger, { type: 'watch_folder' }>; onChange: (t: Trigger) => void }) {
  const me = useMe().data!;
  const folders = useWatchFolders(me.permissions.includes('storage.manage')).data?.items ?? [];
  return (
    <div className="field"><label htmlFor="n-watch">Watch folder</label>
      <select id="n-watch" className="select" value={trigger.watchFolderId ?? ''} onChange={(e) => onChange({ type: 'watch_folder', ...(e.target.value ? { watchFolderId: e.target.value } : {}) })}>
        <option value="">Any watch folder</option>
        {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
      </select>
      <span className="help">Runs once for every asset the folder ingests.</span>
    </div>
  );
}

/* ---------------------------------------------------------------- dates (1.0.0-rc.10) */

type DateRule = { fieldId: string; offsetDays: number; time: string; useFieldTime: boolean; timezone: string };

const dateFields = (fields: FieldDefinition[]) => fields.filter((f) => f.type === 'date' || f.type === 'datetime');

/** A new date rule: the model's first date field, on the day at 09:00 in Helsinki. */
function defaultDateRule(fields: FieldDefinition[]): DateRule {
  return { fieldId: dateFields(fields)[0]?.id ?? 'f_shoot_date', offsetDays: 0, time: '09:00', useFieldTime: false, timezone: DEFAULT_SCHEDULE_TIMEZONE };
}

const ZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
})();

/** A date or date-time field ± days at a time of day (or a date-time's own time) in a time zone. */
function DateRuleFields({ idPrefix, rule, fields, onChange }: { idPrefix: string; rule: DateRule; fields: FieldDefinition[]; onChange: (r: DateRule) => void }) {
  const options = dateFields(fields);
  const field = fields.find((f) => f.id === rule.fieldId);
  const own = field?.type === 'datetime' && rule.useFieldTime;
  const days = Math.abs(rule.offsetDays);
  const when = rule.offsetDays < 0 ? 'before' : rule.offsetDays > 0 ? 'after' : 'on';
  const put = (p: Partial<DateRule>) => onChange({ ...rule, ...p });
  return (
    <>
      <div className="field"><label htmlFor={`${idPrefix}-field`}>Date field</label>
        <select id={`${idPrefix}-field`} className="select" data-testid={`${idPrefix}-field`} value={rule.fieldId} onChange={(e) => {
          const f = fields.find((x) => x.id === e.target.value);
          put({ fieldId: e.target.value, useFieldTime: f?.type === 'datetime' ? rule.useFieldTime : false });
        }}>
          {!field && <option value={rule.fieldId}>{rule.fieldId} (not a field of the model)</option>}
          {options.map((f) => <option key={f.id} value={f.id}>{f.label} ({f.type === 'datetime' ? 'date and time' : 'date'})</option>)}
        </select>
        {!options.length && <span className="help">The metadata model has no date or date-time field yet; add one in Administration → Metadata model.</span>}
      </div>
      <div className="field-row">
        <div className="field"><label htmlFor={`${idPrefix}-when`}>When</label>
          <select id={`${idPrefix}-when`} className="select" data-testid={`${idPrefix}-when`} value={when} onChange={(e) => {
            const w = e.target.value;
            put({ offsetDays: w === 'on' ? 0 : (w === 'before' ? -1 : 1) * Math.max(1, days) });
          }}>
            <option value="before">Days before</option>
            <option value="on">On the date</option>
            <option value="after">Days after</option>
          </select>
        </div>
        {when !== 'on' && (
          <div className="field"><label htmlFor={`${idPrefix}-days`}>Days</label>
            <input id={`${idPrefix}-days`} className="input" type="number" min={1} max={3650} data-testid={`${idPrefix}-days`} value={days}
              onChange={(e) => put({ offsetDays: (when === 'before' ? -1 : 1) * Math.min(3650, Math.max(1, Math.round(Number(e.target.value) || 1))) })} />
          </div>
        )}
      </div>
      <div className="field-row">
        <div className="field"><label htmlFor={`${idPrefix}-time`}>At</label>
          <input id={`${idPrefix}-time`} className="input" type="time" data-testid={`${idPrefix}-time`} value={rule.time} disabled={own} onChange={(e) => e.target.value && put({ time: e.target.value.slice(0, 5) })} />
        </div>
        <div className="field"><label htmlFor={`${idPrefix}-tz`}>Time zone</label>
          <input id={`${idPrefix}-tz`} className="input" list={`${idPrefix}-zones`} data-testid={`${idPrefix}-tz`} value={rule.timezone} onChange={(e) => put({ timezone: e.target.value })} />
          <datalist id={`${idPrefix}-zones`}>{ZONES.map((z) => <option key={z} value={z} />)}</datalist>
        </div>
      </div>
      {field?.type === 'datetime' && (
        <label className="toggle-row">
          <input type="checkbox" checked={rule.useFieldTime} data-testid={`${idPrefix}-own-time`} onChange={(e) => put({ useFieldTime: e.target.checked })} />
          <span><strong>At the field’s own time</strong><span className="help" style={{ display: 'block' }}>Instead of a time of day: “{field.label}” has a time of its own.</span></span>
        </label>
      )}
      <span className="help">{describeDateRule(rule, field?.label ?? rule.fieldId)}. Summer time is followed: the time of day stays the same on both sides of a change.</span>
    </>
  );
}

/** The Date in metadata trigger: its rule, catch-up, dates already passed, conditions and the preview. */
function DateTrigger({ trigger, fields, definitionId, onChange }: {
  trigger: MetadataDateTrigger; fields: FieldDefinition[]; definitionId?: string; onChange: (t: MetadataDateTrigger) => void;
}) {
  const [preview, setPreview] = useState<DateFiringPreviewDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const put = (p: Partial<MetadataDateTrigger>) => { setPreview(null); onChange({ ...trigger, ...p }); };
  const show = async () => {
    if (!definitionId) return;
    setLoading(true);
    setError(null);
    try {
      setPreview(await api<DateFiringPreviewDTO>(`/api/v1/automations/${definitionId}/date-preview`, { method: 'POST', json: { trigger } }));
    } catch (err) {
      setPreview(null);
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <DateRuleFields idPrefix="n-date" rule={trigger} fields={fields} onChange={(r) => put(r)} />
      <div className="field"><label htmlFor="n-date-catchup">Catch up within (hours)</label>
        <input id="n-date-catchup" className="input" type="number" min={0} max={720} data-testid="n-date-catchup" value={trigger.catchUpHours}
          onChange={(e) => put({ catchUpHours: Math.min(720, Math.max(0, Math.round(Number(e.target.value) || 0))) })} />
        <span className="help">If PRISM was down when a date came due, it still runs this long afterwards; later than that it is skipped and logged.</span>
      </div>
      <label className="toggle-row">
        <input type="checkbox" checked={trigger.pastDates} data-testid="n-date-past" onChange={(e) => put({ pastDates: e.target.checked })} />
        <span><strong>Also for dates already passed</strong><span className="help" style={{ display: 'block' }}>Off: only dates still ahead when the workflow is published (or the date is entered) fire. On: dates already passed fire once too, when it is published.</span></span>
      </label>
      <div className="field"><label>And only when…</label>
        {trigger.conditions.length > 0
          ? <Conditions all={trigger.conditions} paths={[...assetPaths(fields), { path: 'trigger.date', label: 'Trigger · the date that came due' }]} allowEmpty onChange={(conditions) => put({ conditions })} />
          : <button type="button" className="btn ghost sm" style={{ alignSelf: 'flex-start' }} data-testid="add-date-condition" onClick={() => put({ conditions: [{ path: 'asset.status', op: 'equals', value: 'ready' }] })}><Plus size={13} /> Condition</button>}
        <span className="help">Checked when the date comes due, e.g. the asset’s class, status, QC result or a field. When they do not hold, that firing is skipped.</span>
      </div>
      <div className="field">
        <button type="button" className="btn sm" style={{ alignSelf: 'flex-start' }} data-testid="date-preview" disabled={!definitionId || loading} onClick={() => void show()}>
          {loading ? <Loader2 size={13} className="spin" /> : <CalendarClock size={13} />} Show the next 20 firings
        </button>
        {error && <span className="error-text" role="alert">{error}</span>}
        {preview && (
          <div data-testid="date-preview-list">
            {preview.items.length === 0 && <span className="muted">No asset has a date that comes due under this rule.</span>}
            <ol className="date-preview">
              {preview.items.map((i) => (
                <li key={`${i.assetId}-${i.firingAt}`} className={i.conditionsMet ? '' : 'faint'}>
                  <span className="mono">{formatInZone(Date.parse(i.firingAt), preview.timezone).replace(` (${preview.timezone})`, '')}</span>
                  <Link to={`/assets/${i.assetId}`}>{i.assetTitle}</Link>
                  <span className="faint mono">{i.date}{i.conditionsMet ? '' : ' · conditions not met now'}</span>
                </li>
              ))}
            </ol>
            <span className="help">Times in {preview.timezone}. Conditions are checked again when each one comes due.{trigger.pastDates && preview.pastDue > 0 ? ` ${preview.pastDue} date${preview.pastDue === 1 ? '' : 's'} already passed would fire once when this is published.` : ''}</span>
          </div>
        )}
      </div>
    </>
  );
}

/** A role, an access group or one person (as tasks and QC review tasks). */
function TaskAssignee({ id, value, onChange }: { id: string; value: QcTaskAssignee; onChange: (a: QcTaskAssignee) => void }) {
  const a = useAssignees().data;
  const v = value.type === 'role' ? `role:${value.key}` : `${value.type}:${value.id}`;
  return (
    <select id={id} className="select" data-testid="create-task-assignee" value={v} onChange={(e) => {
      const [type, key] = [e.target.value.slice(0, e.target.value.indexOf(':')), e.target.value.slice(e.target.value.indexOf(':') + 1)];
      onChange(type === 'role' ? { type: 'role', key } : { type: type as 'group' | 'user', id: key });
    }}>
      {a && !(value.type === 'role' ? a.roles.some((r) => r.key === value.key) : value.type === 'group' ? a.groups.some((g) => g.id === value.id) : a.people.some((p) => p.id === value.id)) && <option value={v}>{value.type === 'role' ? value.key : 'No longer here'}</option>}
      {a && a.roles.length > 0 && <optgroup label="Roles">{a.roles.map((r) => <option key={r.key} value={`role:${r.key}`}>{r.name} ({r.members})</option>)}</optgroup>}
      {a && a.groups.length > 0 && <optgroup label="Groups">{a.groups.map((g) => <option key={g.id} value={`group:${g.id}`}>{g.name} ({g.members})</option>)}</optgroup>}
      <optgroup label="People">{a?.people.map((p) => <option key={p.id} value={`user:${p.id}`}>{p.name}</option>)}</optgroup>
    </select>
  );
}
